import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Award,
  Percent,
  Save,
  RefreshCcw,
  ShieldCheck,
  Timer,
  Truck,
  Wrench,
  Info,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

const BonusPercentage = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  // ✅ Primary button style (always consistent color)
  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const [loading, setLoading] = useState(false);

  // fields (same as table)
  const [mreOneYear, setMreOneYear] = useState(""); // %
  const [lessOneYear, setLessOneYear] = useState(""); // %

  const [gt1EvPq, setGt1EvPq] = useState(""); // days
  const [gt1Ws, setGt1Ws] = useState(""); // days

  const [lt1Above7EvPq, setLt1Above7EvPq] = useState(""); // days
  const [lt1Above7Ws, setLt1Above7Ws] = useState(""); // days

  const [lt6EvPq, setLt6EvPq] = useState(""); // days
  const [lt6Ws, setLt6Ws] = useState(""); // days

  const pageBadge = useMemo(() => "INFRA SCREEN : C-1", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const sectionHead =
    `flex items-center justify-between px-6 py-5 ` +
    (isDark ? "border-b border-white/10" : "border-b border-gray-200");

  const sectionBody = `p-6`;

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const loadData = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/bonusPercentage/bonus-percentage`, {
        params: { finYearId: Number(finYearId) },
      });

      if (res?.data?.success) {
        const r = res.data.row || {};

        setMreOneYear(r.fld_mreoneyear ?? "");
        setLessOneYear(r.fld_lessoneyear ?? "");

        setGt1EvPq(r.fld_mreoneyearevpq ?? "");
        setGt1Ws(r.fld_mreoneyearws ?? "");

        setLt1Above7EvPq(r.fld_lessoneyearabove7evpq ?? "");
        setLt1Above7Ws(r.fld_lessoneyearabove7ws ?? "");

        setLt6EvPq(r.fld_lessoneyearbelow6evpq ?? "");
        setLt6Ws(r.fld_lessoneyearbelow6ws ?? "");
      } else {
        toast.error(res?.data?.message || "Failed to load data.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validate = () => {
    // ASP blocks only if ALL empty (we keep same)
    const allEmpty = [
      mreOneYear,
      lessOneYear,
      gt1EvPq,
      gt1Ws,
      lt1Above7EvPq,
      lt1Above7Ws,
      lt6EvPq,
      lt6Ws,
    ].every((v) => String(v ?? "").trim() === "");

    if (allEmpty) {
      toast.warn("Please enter the values for updation");
      return false;
    }

    const numericFields = [
      { name: "More than 1 Year (%)", v: mreOneYear },
      { name: "Less than 1 Year (%)", v: lessOneYear },
      { name: "GT 1 Year EV/PQ (days)", v: gt1EvPq },
      { name: "GT 1 Year WS (days)", v: gt1Ws },
      { name: "LT 1 Year Above 7m EV/PQ (days)", v: lt1Above7EvPq },
      { name: "LT 1 Year Above 7m WS (days)", v: lt1Above7Ws },
      { name: "LT 6 Months EV/PQ (days)", v: lt6EvPq },
      { name: "LT 6 Months WS (days)", v: lt6Ws },
    ];

    for (const f of numericFields) {
      const s = String(f.v ?? "").trim();
      if (s !== "" && Number.isNaN(Number(s))) {
        toast.error(`${f.name} must be numeric`);
        return false;
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!validate()) return;

    try {
      setLoading(true);

      const payload = {
        fld_mreoneyear: String(mreOneYear ?? "").trim(),
        fld_lessoneyear: String(lessOneYear ?? "").trim(),
        fld_mreoneyearevpq: String(gt1EvPq ?? "").trim(),
        fld_mreoneyearws: String(gt1Ws ?? "").trim(),
        fld_lessoneyearabove7evpq: String(lt1Above7EvPq ?? "").trim(),
        fld_lessoneyearabove7ws: String(lt1Above7Ws ?? "").trim(),
        fld_lessoneyearbelow6evpq: String(lt6EvPq ?? "").trim(),
        fld_lessoneyearbelow6ws: String(lt6Ws ?? "").trim(),
      };

      const res = await axios.put(`${BASE_URL}/api/bonusPercentage/bonus-percentage`, payload, {
        params: { finYearId: Number(finYearId) },
      });

      if (res?.data?.success) {
        toast.success(res?.data?.message || "Successfully Updated");
      } else {
        toast.error(res?.data?.message || "Update failed");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PayRollLayoutWrapper>
      {/* ✅ Toast outside container */}
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BONUS PERCENTAGE</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <Award className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Bonus Percentage Settings
                </div>
                <div className={`text-sm flex items-center gap-2 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  <Info className="w-4 h-4" />
                  Single master row (tbl_BonusPercentage_mas)
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {pageBadge}
              </div>
            </div>

            {/* ✅ ONLY TWO BUTTONS */}
            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={loadData} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handleSave} className={primaryBtn} disabled={loading}>
                <Save className="w-4 h-4" />
                Save Changes
              </button>
            </div>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Bonus Percentage Card */}
            <div className={cardBase}>
              <div className={sectionHead}>
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-2xl flex items-center justify-center ${
                      isDark ? "bg-white/10" : "bg-gray-100"
                    }`}
                  >
                    <Percent className={isDark ? "text-white" : "text-gray-700"} />
                  </div>
                  <div>
                    <div className="text-lg font-extrabold">Bonus Percentage</div>
                    <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      Percentage by service period
                    </div>
                  </div>
                </div>

                <div
                  className={`px-3 py-1 rounded-full text-xs font-bold ${
                    isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                  }`}
                >
                  Required
                </div>
              </div>

              <div className={sectionBody}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className={labelBase}>More Than 1 Year (%)</label>
                    <div className="mt-2 relative">
                      <input
                        type="number"
                        step="0.01"
                        value={mreOneYear}
                        onChange={(e) => setMreOneYear(e.target.value)}
                        placeholder="e.g. 8.33"
                        className={inputBase}
                      />
                      <span
                        className={`absolute right-4 top-1/2 -translate-y-1/2 text-sm font-bold ${
                          isDark ? "text-gray-300" : "text-gray-500"
                        }`}
                      >
                        %
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className={labelBase}>Less Than 1 Year (%)</label>
                    <div className="mt-2 relative">
                      <input
                        type="number"
                        step="0.01"
                        value={lessOneYear}
                        onChange={(e) => setLessOneYear(e.target.value)}
                        placeholder="e.g. 5.00"
                        className={inputBase}
                      />
                      <span
                        className={`absolute right-4 top-1/2 -translate-y-1/2 text-sm font-bold ${
                          isDark ? "text-gray-300" : "text-gray-500"
                        }`}
                      >
                        %
                      </span>
                    </div>
                  </div>
                </div>

              
              </div>
            </div>

            {/* Batta Days Card */}
            <div className={cardBase}>
              <div className={sectionHead}>
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-2xl flex items-center justify-center ${
                      isDark ? "bg-white/10" : "bg-gray-100"
                    }`}
                  >
                    <Timer className={isDark ? "text-white" : "text-gray-700"} />
                  </div>
                  <div>
                    <div className="text-lg font-extrabold">Batta Days</div>
                    <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      Days by department group
                    </div>
                  </div>
                </div>

                <div
                  className={`px-3 py-1 rounded-full text-xs font-bold ${
                    isDark ? "bg-purple-900/40 text-purple-100" : "bg-purple-100 text-purple-800"
                  }`}
                >
                  EV/PQ & WS
                </div>
              </div>

              <div className={sectionBody}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className={labelBase}>Greater than 1 Year EV, PQ (days)</label>
                    <div className="mt-2 flex items-center gap-2">
                      <Truck className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                      <input
                        type="number"
                        step="1"
                        value={gt1EvPq}
                        onChange={(e) => setGt1EvPq(e.target.value)}
                        placeholder="e.g. 30"
                        className={inputBase}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelBase}>Greater than 1 Year WS (days)</label>
                    <div className="mt-2 flex items-center gap-2">
                      <Wrench className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                      <input
                        type="number"
                        step="1"
                        value={gt1Ws}
                        onChange={(e) => setGt1Ws(e.target.value)}
                        placeholder="e.g. 25"
                        className={inputBase}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelBase}>Less than 1 Year (Above 7 month) EV, PQ (days)</label>
                    <div className="mt-2 flex items-center gap-2">
                      <Truck className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                      <input
                        type="number"
                        step="1"
                        value={lt1Above7EvPq}
                        onChange={(e) => setLt1Above7EvPq(e.target.value)}
                        placeholder="e.g. 20"
                        className={inputBase}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelBase}>Less than 1 Year (Above 7 month) WS (days)</label>
                    <div className="mt-2 flex items-center gap-2">
                      <Wrench className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                      <input
                        type="number"
                        step="1"
                        value={lt1Above7Ws}
                        onChange={(e) => setLt1Above7Ws(e.target.value)}
                        placeholder="e.g. 18"
                        className={inputBase}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelBase}>Less than 6 months EV, PQ (days)</label>
                    <div className="mt-2 flex items-center gap-2">
                      <Truck className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                      <input
                        type="number"
                        step="1"
                        value={lt6EvPq}
                        onChange={(e) => setLt6EvPq(e.target.value)}
                        placeholder="e.g. 10"
                        className={inputBase}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelBase}>Less than 6 months WS (days)</label>
                    <div className="mt-2 flex items-center gap-2">
                      <Wrench className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                      <input
                        type="number"
                        step="1"
                        value={lt6Ws}
                        onChange={(e) => setLt6Ws(e.target.value)}
                        placeholder="e.g. 8"
                        className={inputBase}
                      />
                    </div>
                  </div>
                </div>

                {/* ✅ Removed extra submit button here */}
              </div>
            </div>
          </div>

          {/* ✅ Removed bottom Reload/Save button bar completely */}
          <div className={`mt-6 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
            Tip: This page updates the master configuration used during bonus/batta calculation.
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default BonusPercentage;
